import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import {
  createSnapshotMutation,
  deleteSnapshotMutation,
  getSnapshotOptions,
  getSnapshotQueryKey,
  listSnapshotsOptions,
  listSnapshotsQueryKey,
  purgeSnapshotsMutation,
  updateSnapshotMutation,
} from '../generated/api/@tanstack/react-query.gen.js';
import type { SnapshotCreate, SnapshotPurgeRequest, SnapshotUpdate } from '../generated/api/types.gen.js';
import { adaptMutation } from './adapt-mutation.js';

/** List debug snapshots for a template, optionally filtered by step */
export function useSnapshots(templateId: number | undefined, stepId?: string) {
  const query = useQuery({
    ...listSnapshotsOptions({ query: { templateId: templateId ?? 0, stepId, limit: 50 } }),
    enabled: !!templateId,
  });
  if (query.isError) console.error('useSnapshots: failed to load snapshots', query.error);
  return query;
}

/** Get a single snapshot by ID */
export function useSnapshot(id: number | undefined) {
  return useQuery({
    ...getSnapshotOptions({ path: { id: String(id) } }),
    enabled: !!id,
  });
}

/** Save a new debug snapshot */
export function useSaveSnapshot() {
  const qc = useQueryClient();
  return useMutation({
    ...adaptMutation(createSnapshotMutation(), (body: SnapshotCreate) => ({ body })),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: listSnapshotsQueryKey({ query: { templateId: data.templateId } }) });
    },
  });
}

/** Update label or pinned status */
export function useUpdateSnapshot() {
  const qc = useQueryClient();
  return useMutation({
    ...adaptMutation(updateSnapshotMutation(), ({ id, ...body }: { id: number } & SnapshotUpdate) => ({
      path: { id: String(id) },
      body,
    })),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: listSnapshotsQueryKey({ query: { templateId: data.templateId } }) });
      qc.invalidateQueries({ queryKey: getSnapshotQueryKey({ path: { id: String(data.id) } }) });
    },
  });
}

/** Every cached snapshot list, whichever template or step it was loaded for. */
function isSnapshotList(queryKey: readonly unknown[]): boolean {
  const head = queryKey[0];
  return (
    typeof head === 'object' &&
    head !== null &&
    '_id' in head &&
    head._id === listSnapshotsQueryKey({ query: { templateId: 0 } })[0]._id
  );
}

/** Delete a snapshot */
export function useDeleteSnapshot() {
  const qc = useQueryClient();
  return useMutation({
    ...adaptMutation(deleteSnapshotMutation(), (id: number) => ({ path: { id: String(id) } })),
    onSuccess: () => {
      qc.invalidateQueries({ predicate: (query) => isSnapshotList(query.queryKey) });
    },
  });
}

/** Bulk purge snapshots */
export function usePurgeSnapshots() {
  const qc = useQueryClient();
  return useMutation({
    ...adaptMutation(purgeSnapshotsMutation(), (body: SnapshotPurgeRequest) => ({ body })),
    onSuccess: (_data, { templateId }) => {
      qc.invalidateQueries({ queryKey: listSnapshotsQueryKey({ query: { templateId } }) });
    },
  });
}

/**
 * Build a stepResults map from the latest snapshot per step.
 * Returns Record<stepId, { output, contractHash, snapshotId }>.
 */
export function useStepResultsFromSnapshots(templateId: number | undefined) {
  const { data, ...rest } = useSnapshots(templateId);

  const stepResults = useMemo(() => {
    const results: Record<
      string,
      {
        output: unknown;
        contractHash: string | null;
        snapshotId: number;
        createdAt: string;
      }
    > = {};

    if (data?.data) {
      // data is sorted by completedAt desc — first occurrence per step is latest
      for (const snap of data.data) {
        if (!results[snap.stepId]) {
          results[snap.stepId] = {
            output: snap.outputJson ?? snap.output,
            contractHash: snap.contractHash,
            snapshotId: snap.id,
            createdAt: snap.createdAt,
          };
        }
      }
    }

    return results;
  }, [data]);

  return { stepResults, ...rest };
}
